// src/pages/payment-links/PaymentLinkDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardBody, CardHeader, Badge, Button, Alert, Spinner
} from "reactstrap";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { formatAmountWithCurrency } from "../modals/payment/utils/currency";
import BackButton from "../../components/Common/BackButton";
import { useAuth } from "../../hooks/useAuth";
import { getPaymentWords } from "../../utils/paymentWord";

const NAVY = "#0A316E";
const ORANGE = "#FFA500";

/* ============= Helpers ============= */
const formatDateTime = (date) =>
    date
        ? new Date(date).toLocaleString("tr-TR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        })
        : "-";

const isExpired = (expiryDate) => (expiryDate ? new Date(expiryDate) < new Date() : false);

const getStatusInfo = (d) => {
    if (!d) return { color: "secondary", text: "-", icon: "bx-dots-horizontal-rounded" };
    if (d.valid === false) return { color: "dark", text: "Geçersiz", icon: "bx-x-circle" };
    if (isExpired(d.expiryDate)) return { color: "dark", text: "Süresi Dolmuş", icon: "bx-time" };
    if (d.userPaid) return { color: "success", text: "Tamamlandı", icon: "bx-check-circle" };
    if (d.userClicked) return { color: "warning", text: "Beklemede", icon: "bx-hourglass" };
    return { color: "info", text: "Aktif", icon: "bx-link-alt" };
};

const formatAmountTRY = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const n = typeof val === "string" ? parseFloat(val) : val;
    if (isNaN(n)) return "-";
    return `₺ ${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const copyToClipboard = (text) => {
    if (!text) return;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
};

const getAccountTypeInTurkish = (t) => {
    const map = {
        SOLE_PROPRIETORSHIP: "Şahıs Şirketi",
        JOINT_STOCK_COMPANY: "Anonim Şirket",
        LIMITED_COMPANY: "Limited Şirket",
        COOPERATIVE: "Kooperatif",
        GENERAL_PARTNERSHIP: "Kolektif Şirket",
        COMMANDITE_COMPANY: "Komandit Şirket",
        INDIVIDUAL: "Bireysel",
        CORPORATE: "Kurumsal",
    };
    return (t && map[t]) || t || "-";
};

/* ============= Tiny Composites ============= */
const Section = ({ icon, title, children, className = "" }) => (
    <Card className={`rounded-4 border-0 shadow-sm ${className}`}>
        <CardHeader className="bg-white rounded-top-4 border-0 pb-0">
            <h6 className="mb-0 d-flex align-items-center gap-2">
                {icon && <i className={`bx ${icon}`} />} {title}
            </h6>
        </CardHeader>
        <CardBody>{children}</CardBody>
    </Card>
);

const KV = ({ label, children }) => (
    <tr>
        <th style={{ width: "40%" }}>{label}</th>
        <td>{children ?? "-"}</td>
    </tr>
);

const CopyOpenField = ({ value, open, copy = true, mono = true, small = true }) => {
    if (!value) return <span>-</span>;
    return (
        <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className={`bg-light rounded p-2 ${mono ? "font-monospace" : ""} ${small ? "small" : ""} text-break`}>
                {value}
            </div>
            {copy && (
                <Button color="light" size="sm" className="btn-rounded" onClick={() => copyToClipboard(value)} title="Kopyala">
                    <i className="bx bx-copy" />
                </Button>
            )}
            {open && (
                <Button
                    size="sm"
                    className="btn-rounded"
                    style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                    onClick={() => window.open(value, "_blank")}
                    title="Yeni sekmede aç"
                >
                    <i className="bx bx-link-external" />
                </Button>
            )}
        </div>
    );
};

const Pill = ({ ok, onText = "Evet", offText = "Hayır" }) => (
    <span className={`badge bg-${ok ? "success" : "secondary"}`}>{ok ? onText : offText}</span>
);

/* CompaniesTable tarzı minik kart */
const MiniStatCard = ({ icon, label, value, color = "#f8f9fa", text = "#212529" }) => (
    <div
        className="rounded-4 shadow-lg p-3 d-flex align-items-center justify-content-between"
        style={{ background: color, color: text, boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    >
        <div>
            <div className="small text-muted">{label}</div>
            <div className="fw-semibold fs-6">{value}</div>
        </div>
        <i className={`bx ${icon} fs-3`} />
    </div>
);

/* ============= Page ============= */
const PaymentLinkDetailPage = () => {
    const { id } = useParams();
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { roles } = useAuth();
    const pw = getPaymentWords(roles);

    useEffect(() => {
        document.title = `${pw.Word} Linki Detayı | Tahsilat360`;
    }, [pw.Word]);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            try {
                setLoading(true);
                setError("");
                const res = await axios.get(`${API.instant_link_info}/${id}`, {
                    validateStatus: (status) => status === 200 || status === 410 || status < 400,
                });

                if (!alive) return;
                if (res.status === 410) {
                    setD(res.data?.data || res.data);
                } else {
                    setD(res.data?.data || res.data);
                }
            } catch (e) {
                setError(e?.response?.data?.message || `${pw.Word} linki detayı alınamadı.`);
            } finally {
                if (alive) setLoading(false);
            }
        };

        if (id) run();
        return () => {
            alive = false;
        };
    }, [id]);

    const statusInfo = useMemo(() => getStatusInfo(d), [d]);

    const styles = `
    .payment-link-detail .btn-navy { background:${NAVY}; border-color:${NAVY}; color:#fff; }
    .payment-link-detail .btn-navy:hover { filter:brightness(1.03); }
    .payment-link-detail .rounded-4 { border-radius:1rem!important; }
    .payment-link-detail .pill { border-radius:999px!important; }

    .payment-link-detail table {
      width:100%; border-collapse:separate; border-spacing:0;
      border:1px solid #dee2e6; border-radius:1rem; overflow:hidden; background:#fff;
    }
    .payment-link-detail table th {
      background:#f9fafb; color:#495057; font-weight:600;
      border-bottom:1px solid #dee2e6; border-right:1px solid #dee2e6;
    }
    .payment-link-detail table td {
      border-bottom:1px solid #e9ecef; border-right:1px solid #e9ecef; color:#212529; background:#fff;
    }
    .payment-link-detail table tr:last-child td { border-bottom:none; }
    .payment-link-detail table tr td:last-child, .payment-link-detail table tr th:last-child { border-right:none; }
    .payment-link-detail table td, .payment-link-detail table th { padding:.85rem 1rem; vertical-align:middle; }
    .payment-link-detail table tr:hover td { background:#f8f9fa; transition:background .2s ease-in-out; }

    .payment-link-detail .kv-table { margin-bottom:0; }
    .payment-link-detail .mini-badge { border-radius:999px; }

    .payment-link-detail .stats-grid { display:grid; gap:1rem; grid-template-columns: repeat(4, 1fr); }
    @media (max-width: 992px) { .payment-link-detail .stats-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 576px) { .payment-link-detail .stats-grid { grid-template-columns: 1fr; } }
  `;

    /* Stat kartlarının değerleri & renkleri */
    const statCards = d ? [
        {
            icon: d.valid ? "bx-check-circle" : "bx-error-circle",
            label: "Geçerlilik",
            value: d.valid ? "Geçerli" : "Geçersiz",
            color: d.valid ? "#E9F7EF" : "#F3F4F6",
            text: d.valid ? "#166534" : "#111827",
        },
        {
            icon: statusInfo.icon,
            label: "Durum",
            value: statusInfo.text, // Aktif / Beklemede / Tamamlandı / Süresi Dolmuş
            color: statusInfo.color === "success" ? "#E9F7EF"
                : statusInfo.color === "warning" ? "#FFF7ED"
                    : statusInfo.color === "dark" ? "#F3F4F6"
                        : "#E0F2FE",
            text: "#111827",
        },
        {
            icon: d.userClicked ? "bx-mouse-alt" : "bx-mouse",
            label: "Tıklanma",
            value: d.userClicked ? "Tıklandı" : "Tıklanmadı",
            color: d.userClicked ? "#EFF6FF" : "#F3F4F6",
            text: "#111827",
        },
        {
            icon: d.userPaid ? "bx-credit-card" : "bx-credit-card-alt",
            label: "Ödeme",
            value: d.userPaid ? "Ödendi" : "Ödenmedi",
            color: d.userPaid ? "#E9F7EF" : "#FFF1F2",
            text: d.userPaid ? "#166534" : "#9f1239",
        },
    ] : [];

    return (
        <div className="page-content payment-link-detail">
            <style>{styles}</style>
            <div className="container-fluid">
                {/* Üst başlık / aksiyonlar — alttaki detay kartıyla aynı beyaz yüzey */}
                <Card className="bg-white border-0 shadow-lg rounded-4 mb-3">
                    <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-3 px-4">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div className="d-flex align-items-center text-muted small">
                                <span className="text-secondary fw-bold">{pw.Word} Linkleri</span>
                                <span className="mx-2">/</span>
                                <span className="fw-semibold">{pw.Word} Linki Detayı</span>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            {d?.fullUrl && (
                                <>
                                    <Button
                                        size="sm"
                                        className="rounded-pill px-3"
                                        color="light"
                                        onClick={() => copyToClipboard(d.fullUrl)}
                                        title="Bağlantıyı kopyala"
                                    >
                                        <i className="bx bx-copy me-1" /> Kopyala
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="rounded-pill px-3"
                                        style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                                        onClick={() => window.open(d.fullUrl, "_blank")}
                                        title="Yeni sekmede aç"
                                    >
                                        <i className="bx bx-link-external me-1" /> Linki Aç
                                    </Button>
                                </>
                            )}
                        </div>
                    </CardBody>
                </Card>

                <Row>
                    <Col lg={12}>
                        <Card className="bg-white border-0 shadow-lg rounded-4">
                            <CardHeader className="bg-white border-0 pb-0 rounded-top-4">
                                <h5 className="mb-0 text-dark fw-semibold">
                                    <i className="bx bx-link-alt me-2" />
                                    {pw.Word} Linki Detayı
                                </h5>
                                {d?.id && (
                                    <small className="text-muted d-block mt-1">
                                        Link ID: <code className="text-dark">{d.id}</code>
                                    </small>
                                )}
                            </CardHeader>

                            <CardBody className="p-4">
                                {error && (
                                    <Alert color="danger" className="border-0 rounded-4">
                                        <i className="bx bx-error me-2" />
                                        {error}
                                        <Button size="sm" color="danger" className="ms-2 pill" onClick={() => window.location.reload()}>
                                            Tekrar Dene
                                        </Button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner color="primary" />
                                        <div className="mt-2 text-muted">{pw.Word} linki yükleniyor...</div>
                                    </div>
                                ) : (
                                    d && (
                                        <>
                                            {/* === İSTATİSTİK KARTLARI (CompaniesTable görünümü) === */}
                                            <div className="stats-grid mb-4">
                                                {statCards.map((s, i) => (
                                                    <MiniStatCard key={i} icon={s.icon} label={s.label} value={s.value} color={s.color} text={s.text} />
                                                ))}
                                            </div>

                                            {/* === İçerik === */}
                                            <Row className="g-4">
                                                {/* SOL */}
                                                <Col md={6}>
                                                    <Section icon="bx-info-circle" title="Genel Bilgiler">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm kv-table">
                                                                <tbody>
                                                                <KV label="Oluşturulma Tarihi">{formatDateTime(d.createdAt)}</KV>
                                                                <KV label="Son Geçerlilik Tarihi">
                                    <span className={isExpired(d.expiryDate) ? "text-danger" : ""}>
                                      {formatDateTime(d.expiryDate)}
                                        {isExpired(d.expiryDate) && <i className="bx bx-error ms-2" title="Süresi dolmuş" />}
                                    </span>
                                                                </KV>
                                                                <KV label="Geçerlilik"><Pill ok={!!d.valid} onText="Geçerli" offText="Geçersiz" /></KV>
                                                                <KV label="Sabit Tutar">
                                                                    {d.fixedAmount != null ? <span className="text-success fw-bold">{formatAmountWithCurrency(d.fixedAmount, d.currency)}</span> : "-"}
                                                                </KV>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </Section>

                                                    <Section icon="bx-link" title="Bağlantı">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm kv-table">
                                                                <tbody>
                                                                <KV label="Tam URL (fullUrl)">
                                                                    <CopyOpenField value={d.fullUrl} open copy />
                                                                </KV>
                                                                {d.paymentLinkUrl && d.paymentLinkUrl !== d.fullUrl && (
                                                                    <KV label="Kısa/Eski URL">
                                                                        <CopyOpenField value={d.paymentLinkUrl} open copy />
                                                                    </KV>
                                                                )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </Section>

                                                    {d.cardsMaxInstallments && Object.keys(d.cardsMaxInstallments).length > 0 && (
                                                        <Section icon="bx-credit-card" title="Kart Bazlı Maksimum Taksitler" className="mt-4">
                                                            <Row className="g-2">
                                                                {Object.entries(d.cardsMaxInstallments).map(([brand, max]) => (
                                                                    <Col xs={6} md={4} lg={3} key={brand}>
                                                                        <div className="d-flex align-items-center justify-content-between bg-light rounded-3 px-3 py-2">
                                                                            <span className="small fw-600">{brand}</span>
                                                                            <Badge color="secondary" className="mini-badge">{max}</Badge>
                                                                        </div>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        </Section>
                                                    )}
                                                </Col>

                                                {/* SAĞ */}
                                                <Col md={6}>
                                                    <Section icon="bx-user" title="Cari/Bayi Hesap Bilgileri">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm kv-table">
                                                                <tbody>
                                                                <KV label="Cari/Bayi Hesap Adı">{d.accountName || "-"}</KV>
                                                                <KV label="Cari/Bayi Türü">
                                                                    <Badge color="secondary" className="mini-badge">
                                                                        {getAccountTypeInTurkish(d.accountType)}
                                                                    </Badge>
                                                                </KV>
                                                                <KV label="Telefon Numarası">{d.phoneNumber || "-"}</KV>
                                                                {d.accountGroupId && (
                                                                    <KV label="Taksit Grubu ID">
                                                                        <code className="small">{d.accountGroupId}</code>
                                                                    </KV>
                                                                )}
                                                                {/* tıklanma & ödeme burada da yedek gösterim */}
                                                                <KV label="Tıklanma Durumu">
                                    <span className={`badge bg-${d.userClicked ? "success" : "warning"}`}>
                                      {d.userClicked ? "Tıklandı" : "Tıklanmadı"}
                                    </span>
                                                                </KV>
                                                                <KV label={`${pw.Word} Durumu`}>
                                    <span className={`badge bg-${d.userPaid ? "success" : "danger"}`}>
                                      {d.userPaid ? "Ödendi" : "Ödenmedi"}
                                    </span>
                                                                </KV>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </Section>

                                                    <Section icon="bx-receipt" title="Fatura Snapshot Bilgileri" className="mt-4">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm kv-table">
                                                                <tbody>
                                                                <KV label="Fatura Şirket Adı">{d.invoiceCompanyName || "-"}</KV>
                                                                <KV label="Vergi Numarası"><code className="small">{d.invoiceTaxNumber || "-"}</code></KV>
                                                                <KV label="TC Numarası"><code className="small">{d.invoiceTcNumber || "-"}</code></KV>
                                                                <KV label="Vergi Dairesi">{d.invoiceTaxOffice || "-"}</KV>
                                                                <KV label="Adres">{d.invoiceAddress || "-"}</KV>
                                                                <KV label="Fatura Hesap Türü">
                                                                    <Badge color="secondary" className="mini-badge">
                                                                        {getAccountTypeInTurkish(d.invoiceAccountType)}
                                                                    </Badge>
                                                                </KV>
                                                                <KV label="Fatura E-posta">{d.invoiceEmail || "-"}</KV>
                                                                <KV label="Fatura Telefon">{d.invoicePhone || "-"}</KV>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </Section>

                                                    {(d.cancellationReturnPrivacy || d.personalDataPolicy || d.serviceContract || d.onlineCollectionContract) && (
                                                        <Section icon="bx-file" title="Sözleşme Metinleri" className="mt-4">
                                                            {d.cancellationReturnPrivacy && (
                                                                <div className="mb-3">
                                                                    <div className="fw-600 mb-1">İptal / İade Koşulları & Gizlilik</div>
                                                                    <pre style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {d.cancellationReturnPrivacy}
                                  </pre>
                                                                </div>
                                                            )}
                                                            {d.personalDataPolicy && (
                                                                <div className="mb-3">
                                                                    <div className="fw-600 mb-1">Kişisel Veriler Politikası</div>
                                                                    <pre style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "word-break" }}>
                                    {d.personalDataPolicy}
                                  </pre>
                                                                </div>
                                                            )}
                                                            {d.serviceContract && (
                                                                <div className="mb-3">
                                                                    <div className="fw-600 mb-1">Hizmet Sözleşmesi</div>
                                                                    <pre style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {d.serviceContract}
                                  </pre>
                                                                </div>
                                                            )}
                                                            {d.onlineCollectionContract && (
                                                                <div className="mb-0">
                                                                    <div className="fw-600 mb-1">Online Tahsilat Sözleşmesi</div>
                                                                    <pre style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {d.onlineCollectionContract}
                                  </pre>
                                                                </div>
                                                            )}
                                                        </Section>
                                                    )}
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default PaymentLinkDetailPage;